import React, { useEffect, useState } from "react";
import { useImmerReducer } from "use-immer";

import { assertNativeError } from "@examspark/common";
import { Modal, ModalProps, SubmitButtons } from "@examspark/common-ui";
import { PaddedBody } from "@examspark/common-ui/dist/components/modals/Modal";
import { Resource } from "@examspark/types";

import {
  defaultNewActivityState,
  NewActivityContext,
  newActivityReducer,
  NewActivityState,
} from "../../../../classroom";
import {
  getObjectiveAuthorityIdsFromAppState,
  useAppContext,
} from "../../../../providers/AppContext";
import { safeLocalStorage } from "../../../../safeStorage";
import { useNewActivity } from "../hooks/useNewActivity";

import ExamBuilder from "./ExamBuilder";

type ExamBuilderModalProps = ModalProps & {
  onBuild?: (items: Resource[]) => void;
};

const ExamBuilderModalInner = (props: ExamBuilderModalProps) => {
  const [isBuilding, setIsBuilding] = useState(false);
  const newActivity = useNewActivity();
  const [appState] = useAppContext();

  const objectiveAuthorityIds = getObjectiveAuthorityIdsFromAppState(appState);

  const handleRequestClose = () => {
    window.confirm("Are you sure you want to cancel?") &&
      props.onRequestClose?.();
  };

  const handleBuildExam = async () => {
    try {
      setIsBuilding(true);
      // just builds an activity, doesn't actually save it to ddb
      const questions = await newActivity.buildActivity();
      if (!questions) throw new Error("No questions found.");
      props.onBuild?.(questions);
      setIsBuilding(false);
    } catch (error) {
      assertNativeError(error);
      alert(error.message);
      setIsBuilding(false);
    }
  };

  return (
    <>
      <Modal
        size="large"
        title="Assessment Builder"
        buttons={
          <SubmitButtons
            onCancel={handleRequestClose}
            isLoading={isBuilding}
            onSubmit={handleBuildExam}
            submitLabel="Build Assessment"
          />
        }
        {...props}
        onRequestClose={handleRequestClose}
      >
        <PaddedBody>
          <ExamBuilder
            objectiveAuthorityId={objectiveAuthorityIds?.[0] ?? "tea"}
          />
        </PaddedBody>
      </Modal>
    </>
  );
};

/**
 * Uses a guided, wizard-like form used to build an exam. This component is used
 * inside of the ActivityEditor.
 */
const ExamBuilderModal = (props: ExamBuilderModalProps) => {
  const localStorageKey = "examspark:examBuilderDefaults";
  const [newActivityState, dispatchNewActivity] =
    useImmerReducer<NewActivityState>(
      newActivityReducer,
      defaultNewActivityState,
    );

  useEffect(() => {
    if (!newActivityState.document) return;
    if (!newActivityState.sections?.length) return;

    safeLocalStorage.serializeItem(localStorageKey, {
      document: newActivityState.document,
      sections: newActivityState.sections,
    });
  }, [newActivityState.document, newActivityState.sections]);

  return (
    <NewActivityContext.Provider
      value={[newActivityState, dispatchNewActivity]}
    >
      <ExamBuilderModalInner
        {...props}
        onAfterOpen={() => {
          const initialState =
            safeLocalStorage.parseItem<{
              document?: string;
              sections?: string[];
            }>(localStorageKey) ?? {};
          dispatchNewActivity({
            type: "UPDATE",
            data: {
              ...newActivityState,
              ...initialState,
            },
          });
        }}
        onAfterClose={() =>
          dispatchNewActivity({
            type: "RESET",
          })
        }
      />
    </NewActivityContext.Provider>
  );
};

export default ExamBuilderModal;
